import { useState, useEffect } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Video,
  Plus,
  Trash2,
  Copy,
  Check,
  ExternalLink,
  Clock,
  Play,
  Sparkles,
  Mail,
  Users,
  Send,
  Loader2,
  Eye,
  X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EntitlementGate from "@/components/EntitlementGate";

// Email Preview Component
function EmailPreview({ request, onClose, onSend, sending }) {
  const recordingUrl = `${window.location.origin}/video/${request.token}`;
  const expiresDate = request.expires_at 
    ? new Date(request.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'in 14 days';
  
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <div>
            <h3 className="text-lg font-semibold text-indigo-500">Email Preview</h3>
            <p className="text-sm text-indigo-400">To: {request.client_email}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-indigo-400 hover:text-indigo-500 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Email Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="bg-[#0f172a] rounded-xl overflow-hidden">
            {/* Email Header */}
            <div className="text-center py-6">
              <h1 className="text-2xl font-bold text-indigo-500">ClientKit</h1>
              <p className="text-slate-400 text-sm">Freelancer OS</p>
            </div>
            
            {/* Main Content */}
            <div className="mx-4 mb-4 bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-2xl p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-500/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-3xl">🎬</span>
                </div>
                <h2 className="text-xl font-semibold text-indigo-500">
                  Hi {request.client_name || 'there'}!
                </h2>
              </div>
              
              <p className="text-indigo-400 text-center mb-6 leading-relaxed">
                {request.message || "We'd love to hear about your experience working with us!"}
              </p>
              
              {/* Questions */}
              <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-4 mb-6">
                <p className="text-indigo-500 font-medium mb-3">Here are some questions to guide you:</p>
                <div className="space-y-2">
                  {(request.questions || []).map((q, i) => (
                    <div key={i} className="flex items-start gap-3 text-indigo-400">
                      <span className="w-6 h-6 bg-indigo-500 text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                        {i + 1}
                      </span>
                      <span>{q}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <p className="text-slate-400 text-sm text-center mb-6">
                Recording takes just 2-3 minutes. Use your phone or computer webcam.
              </p>
              
              {/* CTA Button Preview */}
              <div className="text-center">
                <span className="inline-block bg-gradient-to-r from-indigo-500 to-indigo-600 text-indigo-500 px-8 py-3 rounded-lg font-semibold">
                  Record My Testimonial
                </span>
              </div>
              
              <p className="text-slate-400 text-xs text-center mt-4">
                This link expires on {expiresDate}
              </p>
            </div>
            
            {/* Footer */}
            <div className="border-t border-slate-700 py-4 text-center">
              <p className="text-slate-400 text-xs">
                Sent via ClientKit<br />
                <span className="text-slate-600">Questions? Reply to this email.</span>
              </p>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-indigo-400 hover:text-indigo-500 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSend}
            disabled={sending}
            className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg font-medium transition-all disabled:opacity-50"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function VideoAdmin({ user }) {
  const [requests, setRequests] = useState([]);
  const [assets, setAssets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [copied, setCopied] = useState(null);
  const [polishing, setPolishing] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [previewRequest, setPreviewRequest] = useState(null);
  
  // New request form
  const [newRequest, setNewRequest] = useState({
    project_id: "",
    client_email: "",
    client_name: "",
    message: "We'd love to hear about your experience working with us!",
    questions: [
      "What challenge were you facing before working with us?",
      "How did our work help solve that challenge?",
      "What results have you seen?"
    ]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reqRes, assetsRes, projRes, clientsRes] = await Promise.all([
        api.get("/video-testimonials/requests").catch(() => ({ data: { requests: [] } })),
        api.get("/video-testimonials/assets").catch(() => ({ data: { assets: [] } })),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/clients").catch(() => ({ data: [] }))
      ]);
      setRequests(reqRes.data.requests || []);
      setAssets(assetsRes.data.assets || []);
      setProjects(projRes.data || []);
      setClients(clientsRes.data || []);
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  };

  const createRequest = async () => {
    if (!newRequest.client_email) {
      toast.error("Please enter the client's email");
      return;
    }
    
    try {
      const res = await api.post("/video-testimonials/requests", {
        project_id: newRequest.project_id || undefined,
        client_email: newRequest.client_email,
        client_name: newRequest.client_name,
        message: newRequest.message,
        questions: newRequest.questions.filter(q => q.trim())
      });
      
      const shareUrl = `${window.location.origin}${res.data.public_url}`;
      await navigator.clipboard.writeText(shareUrl);
      
      setRequests([{ 
        id: res.data.request_id, 
        token: res.data.token,
        client_email: newRequest.client_email,
        client_name: newRequest.client_name,
        status: "pending",
        expires_at: res.data.expires_at,
        created_at: new Date().toISOString()
      }, ...requests]);
      
      setShowNewForm(false);
      setNewRequest({
        project_id: "",
        client_email: "",
        client_name: "",
        message: "We'd love to hear about your experience working with us!",
        questions: [
          "What challenge were you facing before working with us?",
          "How did our work help solve that challenge?",
          "What results have you seen?"
        ]
      });
      
      toast.success("Request created! Link copied to clipboard.");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to create request");
    }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/video/${token}`;
    await navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(null), 2000);
    toast.success("Link copied!");
  };

  const sendEmailRequest = async (requestId) => {
    setSendingEmail(requestId);
    try {
      const res = await api.post(`/video-testimonials/requests/${requestId}/send-email`);
      toast.success(res.data.message || "Email sent successfully!");
      
      // Update request status locally
      setRequests(requests.map(r => 
        r.id === requestId ? { ...r, email_sent: true } : r
      ));
      
      // Close preview modal if open
      setPreviewRequest(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('403')) {
        toast.error("Email service blocked. Please verify your SendGrid sender address.");
      } else {
        toast.error(detail || "Failed to send email");
      }
    } finally {
      setSendingEmail(null);
    }
  };

  const openEmailPreview = (request) => {
    setPreviewRequest(request);
  };

  const deleteRequest = async (requestId) => {
    if (!confirm("Delete this request?")) return;
    
    try {
      await api.delete(`/video-testimonials/requests/${requestId}`);
      setRequests(requests.filter(r => r.id !== requestId));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const polishQuote = async (assetId, transcript) => {
    setPolishing(assetId);
    try {
      const res = await api.post(`/video-testimonials/assets/${assetId}/polish`, {
        transcript
      });
      
      setAssets(assets.map(a => 
        a.id === assetId 
          ? { ...a, polished_quote: res.data.polished_quote, status: "polished" }
          : a
      ));
      
      toast.success("Quote polished!");
    } catch (e) {
      toast.error("Failed to polish quote");
    } finally {
      setPolishing(null);
    }
  };

  const deleteAsset = async (assetId) => {
    if (!confirm("Delete this video?")) return;
    
    try {
      await api.delete(`/video-testimonials/assets/${assetId}`);
      setAssets(assets.filter(a => a.id !== assetId));
      toast.success("Deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const updateQuestion = (index, value) => {
    const updated = [...newRequest.questions];
    updated[index] = value;
    setNewRequest({ ...newRequest, questions: updated });
  };

  const addQuestion = () => {
    setNewRequest({ 
      ...newRequest, 
      questions: [...newRequest.questions, ""] 
    });
  };

  const removeQuestion = (index) => {
    if (newRequest.questions.length > 1) {
      setNewRequest({
        ...newRequest,
        questions: newRequest.questions.filter((_, i) => i !== index)
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <EntitlementGate feature="video" user={user}>
      <div className="space-y-6" data-testid="video-admin">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-indigo-500 flex items-center gap-2">
              <Video className="w-6 h-6 text-red-400" />
              Video Testimonials
            </h1>
            <p className="text-indigo-400 text-sm mt-1">
              Collect video testimonials from your clients
            </p>
          </div>
          
          <button
            onClick={() => setShowNewForm(true)}
            className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Request
          </button>
        </div>

        {/* New Request Form */}
        {showNewForm && (
          <Card className="bg-slate-800/50 border-indigo-500/30">
            <CardHeader>
              <CardTitle className="text-indigo-500">Create Video Request</CardTitle>
              <CardDescription className="text-indigo-400">
                Send a link to your client for them to record a testimonial
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-indigo-400 mb-1">Client Email *</label>
                  <input
                    type="email"
                    value={newRequest.client_email}
                    onChange={(e) => setNewRequest({ ...newRequest, client_email: e.target.value })}
                    placeholder="client@example.com"
                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-indigo-400 mb-1">Client Name</label>
                  <input
                    type="text"
                    value={newRequest.client_name}
                    onChange={(e) => setNewRequest({ ...newRequest, client_name: e.target.value })}
                    placeholder="John Smith"
                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-indigo-400 mb-1">Link to Project</label>
                <Select
                  value={newRequest.project_id}
                  onValueChange={(v) => setNewRequest({ ...newRequest, project_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Select project (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="none" className="text-slate-400">None</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-slate-400">
                        {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm text-indigo-400 mb-1">Welcome Message</label>
                <Textarea
                  value={newRequest.message}
                  onChange={(e) => setNewRequest({ ...newRequest, message: e.target.value })}
                  placeholder="Personal message to your client"
                  className="min-h-[80px] bg-slate-900/50 border-slate-600 text-indigo-500 placeholder:text-slate-400"
                />
              </div>
              
              <div>
                <label className="block text-sm text-indigo-400 mb-2">Questions for Client</label>
                <div className="space-y-2">
                  {newRequest.questions.map((q, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        type="text"
                        value={q}
                        onChange={(e) => updateQuestion(i, e.target.value)}
                        placeholder={`Question ${i + 1}`}
                        className="flex-1 px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
                      />
                      {newRequest.questions.length > 1 && (
                        <button
                          onClick={() => removeQuestion(i)}
                          className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={addQuestion}
                    className="text-sm text-indigo-400 hover:text-indigo-400"
                  >
                    + Add question
                  </button>
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={createRequest}
                  className="flex-1 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  <Mail className="w-5 h-5" />
                  Create & Copy Link
                </button>
                <button
                  onClick={() => setShowNewForm(false)}
                  className="px-6 py-3 text-indigo-400 hover:text-indigo-500 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Requests */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-indigo-400 uppercase tracking-wide">
              Pending Requests ({requests.filter(r => r.status === "pending").length})
            </h3>
            
            {requests.filter(r => r.status !== "uploaded").length === 0 ? (
              <Card className="bg-slate-800/30 border-slate-700/50 border-dashed">
                <CardContent className="py-8 text-center">
                  <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-indigo-400 text-sm">No pending requests</p>
                </CardContent>
              </Card>
            ) : (
              requests.filter(r => r.status !== "uploaded").map((req) => (
                <Card key={req.id} className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-indigo-500">{req.client_name || req.client_email}</p>
                        {req.client_name && (
                          <p className="text-xs text-slate-400">{req.client_email}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {req.email_sent && (
                          <Badge className="bg-green-500/20 text-green-400 text-xs">
                            Sent
                          </Badge>
                        )}
                        <Badge className={
                          req.status === "pending" ? "bg-indigo-500/20 text-indigo-400" :
                          req.status === "uploaded" ? "bg-green-500/20 text-green-400" :
                          "bg-slate-500/20 text-indigo-400"
                        }>
                          {req.status}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex items-center gap-2 mt-3">
                      {req.client_email && !req.email_sent && (
                        <>
                          <button
                            onClick={() => openEmailPreview(req)}
                            className="flex items-center justify-center gap-1.5 py-2 px-3 bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 rounded-lg text-sm transition-colors"
                            title="Preview email"
                            data-testid="preview-email-btn"
                          >
                            <Eye className="w-4 h-4" />
                            Preview
                          </button>
                          <button
                            onClick={() => sendEmailRequest(req.id)}
                            disabled={sendingEmail === req.id}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-indigo-500/20 text-indigo-400 hover:bg-slate-400/30 rounded-lg text-sm transition-colors disabled:opacity-50"
                            title="Send email to client"
                            data-testid="send-email-btn"
                          >
                            {sendingEmail === req.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                            {sendingEmail === req.id ? "Sending..." : "Send"}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => copyLink(req.token)}
                        className="flex items-center justify-center gap-1.5 py-2 px-3 bg-slate-400/50 text-slate-400 hover:bg-slate-600/50 rounded-lg text-sm transition-colors"
                        title="Copy link"
                      >
                        {copied === req.token ? (
                          <>
                            <Check className="w-4 h-4 text-green-400" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy Link
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => deleteRequest(req.id)}
                        className="p-2 text-indigo-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-slate-400 mt-3">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Expires {new Date(req.expires_at).toLocaleDateString()}
                      </span>
                      {req.email_sent && (
                        <span className="text-green-400/70 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          Email sent
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Received Videos */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-indigo-400 uppercase tracking-wide">
              Received Videos ({assets.length})
            </h3>
            
            {assets.length === 0 ? (
              <Card className="bg-slate-800/30 border-slate-700/50 border-dashed">
                <CardContent className="py-8 text-center">
                  <Video className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                  <p className="text-indigo-400 text-sm">No videos received yet</p>
                </CardContent>
              </Card>
            ) : (
              assets.map((asset) => (
                <Card key={asset.id} className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-16 h-16 bg-slate-400 rounded-lg flex items-center justify-center">
                        <Play className="w-8 h-8 text-indigo-400" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-indigo-500">{asset.filename || "Video"}</p>
                        <p className="text-xs text-slate-400">
                          {(asset.size / (1024 * 1024)).toFixed(1)} MB • {new Date(asset.created_at).toLocaleDateString()}
                        </p>
                        <Badge className={
                          asset.status === "polished" ? "bg-green-500/20 text-green-400" :
                          "bg-slate-500/20 text-indigo-400"
                        }>
                          {asset.status}
                        </Badge>
                      </div>
                    </div>
                    
                    {asset.polished_quote && (
                      <div className="p-3 bg-slate-900/50 rounded-lg mb-3">
                        <p className="text-sm text-slate-400 italic">&ldquo;{asset.polished_quote}&rdquo;</p>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      {!asset.polished_quote && (
                        <button
                          onClick={() => {
                            const transcript = prompt("Enter the video transcript:");
                            if (transcript) polishQuote(asset.id, transcript);
                          }}
                          disabled={polishing === asset.id}
                          className="flex-1 py-2 text-sm bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 rounded-lg transition-colors flex items-center justify-center gap-1"
                        >
                          {polishing === asset.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-stone-400" />
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              Polish Quote
                            </>
                          )}
                        </button>
                      )}
                      <button
                        onClick={() => deleteAsset(asset.id)}
                        className="p-2 text-indigo-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
      
      {/* Email Preview Modal */}
      {previewRequest && (
        <EmailPreview
          request={previewRequest}
          onClose={() => setPreviewRequest(null)}
          onSend={() => sendEmailRequest(previewRequest.id)}
          sending={sendingEmail === previewRequest.id}
        />
      )}
    </EntitlementGate>
  );
}
